/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : AccountModal.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Account selection popup
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Dec 23, 2021
 *
 * @Version      : Version 1.0
 *
 * By            : <Author>
 * On            : <Date>
 * Description   : <Change Description>
 *
 *
 ******************************************************************************/

import React, { FC, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Search,
  FormattedList,
  Loader,
  Grid,
  Notification,
} from "@scuf/common";
import { ICustomer } from "models/customer";
import { ErrorNotification } from "components/ErrorNotification";
import {
  setSelectedCustomer,
  selectCustomer,
  selectCustomerList,
  selectLoader,
} from "stores/CustomerStore";
import {
  selectErrorNotification,
  setShowAccountModal,
  selectShowAccountModal,
} from "stores/UIStore";
import "./AccountModal.scss";

export const AccountModal: FC = () => {
  const navigate = useNavigate();
  const isModalOpen = useSelector(selectShowAccountModal);
  const selectedCustomer = useSelector(selectCustomer);
  const { error } = useSelector(selectErrorNotification);
  const [enteredCustomer, setEnteredCustomer] = useState<string>(
    selectedCustomer !== null ? selectedCustomer?.customerName : ""
  );
  const [selectedOptionItem, setSelectedOptionItem] =
    useState<ICustomer | null>(null);
  const customerList = useSelector(selectCustomerList);
  const customerlLoader = useSelector(selectLoader);
  const [searchResults, setSearchResults] = useState<ICustomer[] | null>([]);
  const dispatch = useDispatch();

  const onSearchClickHandler = (value: string | undefined) => {
    if (value !== "" && value !== undefined) {
      const searchFilterResults =
        customerList &&
        customerList.filter((customer: ICustomer) => {
          if (
            customer.customerName
              ?.toLowerCase()
              .includes(enteredCustomer.toLowerCase())
          ) {
            return customer;
          }
        });
      if (searchFilterResults !== null) {
        setSearchResults(searchFilterResults);
        setEnteredCustomer(value);
      }
    } else {
      setSearchResults(customerList);
    }
  };

  useEffect(() => {
    if (selectedCustomer === null) {
      setSearchResults(customerList);
    } else {
      onSearchClickHandler(selectedCustomer?.customerName);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerList]);

  const handleOnOptionItemSelect = (value: ICustomer) => {
    setSelectedOptionItem(value);
    setEnteredCustomer(value.customerName);
  };

  const onCloseHandler = () => {
    dispatch(setShowAccountModal(false));
  };

  const onNextClickHandler = () => {
    dispatch(setSelectedCustomer(selectedOptionItem));
    navigate("/");
    onCloseHandler();
  };

  const resetClear = (): void => {
    setSearchResults(customerList);
    setSelectedOptionItem(null);
    setEnteredCustomer("");
  };

  return (
    <Modal
      className="select-customer-modal"
      size="small"
      closeIcon={true}
      closeOnDimmerClick={false}
      open={isModalOpen}
      onClose={onCloseHandler}
    >
      <Modal.Header>SELECT ACCOUNT</Modal.Header>
      <Modal.Content scrolling={false} className="modal-content">
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <ErrorNotification error={error} />
              {searchResults?.length === 0 ? (
                <Notification
                  hasIcon={true}
                  title={"NO ASSOCIATED ACCOUNTS"}
                  severity="important"
                  className="no-accounts-notification"
                >
                  <span>
                    You&apos;re not associated to any customer accounts to use
                    APM Settings functionality. Please contact{" "}
                    <a href="mailto:dsa@honeywellaerospace.com">{`dsa@honeywellaerospace.com`}</a> for your assistance.
                  </span>
                </Notification>
              ) : null}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <div className="search-container">
                <Search
                  data-testid="customer-search"
                  placeholder="Search Customer"
                  results={[]}
                  className="account-search"
                  value={enteredCustomer}
                  onResultClear={resetClear}
                  onSearchChange={(value) => onSearchClickHandler(value)}
                  onIconClick={(value) => onSearchClickHandler(value)}
                />
                {customerlLoader === true ? (
                  <Loader minHeight={200}>
                    <div className="placeholder" />
                  </Loader>
                ) : (
                  <div className="customer-account-list">
                    <FormattedList>
                      {searchResults &&
                        searchResults.map((customer, index) => {
                          const activeRow =
                            selectedOptionItem !== null &&
                            selectedOptionItem.customerId ===
                              customer.customerId;

                          return (
                            <FormattedList.Item
                              key={`${customer.customerId}${index}`}
                              className={activeRow ? "active-account" : ""}
                              content={customer.customerName}
                              onClick={() => handleOnOptionItemSelect(customer)}
                            />
                          );
                        })}
                    </FormattedList>
                  </div>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Footer>
        <Grid>
          <Grid.Row>
            <Grid.Column className="account-modal-footer-button-container">
              <Button
                type="secondary"
                size="medium"
                className="cancel-customer-popup-button"
                content="Cancel"
                onClick={onCloseHandler}
              />
              <Button
                type="primary"
                size="medium"
                content="Select"
                className="select-customer-popup-button"
                onClick={onNextClickHandler}
                disabled={selectedOptionItem === null}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Footer>
    </Modal>
  );
};
